// A column per day, stacked by whatever the caller bands it with.
//
// It draws what it is given and knows nothing about cost: the caller supplies
// the bands, their colours and the formatter, which is what lets one primitive
// serve a day of money and, later, a day of anything else.
//
// Every chart in this app carries a TABLE of the same numbers, read by screen
// readers and by anybody who cannot separate two of the colours. That is not a
// courtesy: three of the palette's slots sit below 3:1 against white, which is
// allowed only where the figures are also present as text.

import type { ReactNode } from "react";
import styles from "./Chart.module.css";
import { axisMax, bands, showsTick, tickStride, ticks, valueY } from "./scale.js";

export interface StackedBarSegment {
  /** The band's dimension value, e.g. `telephony`. */
  readonly key: string;
  readonly label: string;
  readonly value: number;
  readonly color: string;
}

export interface StackedBar {
  /** The x value, e.g. a day. */
  readonly key: string;
  /** Spoken in the table and the tooltip, e.g. "2026-09-04". */
  readonly label: string;
  /** Drawn on the axis, e.g. "4". */
  readonly tick: string;
  readonly segments: readonly StackedBarSegment[];
}

export interface StackedBarsProps {
  readonly bars: readonly StackedBar[];
  readonly formatValue: (value: number) => string;
  /** Names the chart for assistive tech, e.g. "Cost by day". */
  readonly label: string;
  /** Why there is nothing to draw. Never an empty box. */
  readonly emptyMessage: string;
  /**
   * The plot's height in the same units as its 720-wide box. A panel that
   * fills a window passes its own aspect so the chart fills it too.
   */
  readonly plotHeight?: number;
}

const PLOT_WIDTH = 720;
const DEFAULT_PLOT_HEIGHT = 150;
const AXIS_GUTTER = 44;
const BAR_GAP = 2;

export function StackedBars({
  bars,
  formatValue,
  label,
  emptyMessage,
  plotHeight = DEFAULT_PLOT_HEIGHT,
}: StackedBarsProps): ReactNode {
  if (bars.length === 0) return <div className={styles.empty}>{emptyMessage}</div>;

  const totals = bars.map((bar) => bar.segments.reduce((sum, segment) => sum + segment.value, 0));
  const top = axisMax(Math.max(...totals));
  const laid = bands(bars.length, PLOT_WIDTH - AXIS_GUTTER, BAR_GAP);
  const legend = legendOf(bars);
  const stride = tickStride(
    bars.map((bar) => bar.tick),
    PLOT_WIDTH - AXIS_GUTTER,
  );

  return (
    <figure className={styles.figure}>
      <svg
        className={styles.plot}
        viewBox={`0 0 ${PLOT_WIDTH} ${plotHeight + 18}`}
        role="img"
        aria-label={`${label}. ${bars.length} columns. The same figures are in the table below.`}
      >
        {ticks(top).map((tick) => (
          <g key={tick}>
            <line
              className={styles.grid}
              x1={AXIS_GUTTER}
              x2={PLOT_WIDTH}
              y1={valueY(tick, top, plotHeight)}
              y2={valueY(tick, top, plotHeight)}
            />
            <text
              className={styles.axisText}
              x={AXIS_GUTTER - 6}
              y={valueY(tick, top, plotHeight) + 3}
              textAnchor="end"
            >
              {formatValue(tick)}
            </text>
          </g>
        ))}
        {bars.map((bar, index) => {
          const band = laid[index];
          if (band === undefined) return null;
          let cursor = 0;
          const isLast = index === bars.length - 1 && bars.length > 1;
          return (
            <g key={bar.key}>
              {bar.segments.map((segment) => {
                const from = valueY(cursor, top, plotHeight);
                cursor += segment.value;
                const to = valueY(cursor, top, plotHeight);
                // A 2px surface gap between stacked segments, so two fills that
                // touch cannot read as one.
                const height = Math.max(from - to - (bar.segments.length > 1 ? 2 : 0), 0);
                return (
                  <rect
                    key={segment.key}
                    className={styles.bar}
                    x={AXIS_GUTTER + band.x}
                    y={to}
                    width={band.width}
                    height={height}
                    fill={segment.color}
                  >
                    <title>{`${bar.label} · ${segment.label} · ${formatValue(segment.value)}`}</title>
                  </rect>
                );
              })}
              {showsTick(index, bars.length, stride) && (
                <text
                  className={styles.axisText}
                  // The last label ends on the plot's right edge rather than
                  // hanging half outside the figure.
                  x={AXIS_GUTTER + band.x + (isLast ? band.width : band.width / 2)}
                  y={plotHeight + 14}
                  textAnchor={isLast ? "end" : "middle"}
                >
                  {bar.tick}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {legend.length > 1 && (
        <div className={styles.legend}>
          {legend.map((entry) => (
            <span key={entry.key} className={styles.legendItem}>
              <span
                className={styles.swatch}
                style={{ background: entry.color }}
                aria-hidden="true"
              />
              {entry.label}
            </span>
          ))}
        </div>
      )}

      <div className="srOnly">
        <table>
          <caption>{label}</caption>
          <tbody>
            {bars.map((bar, index) => (
              <tr key={bar.key}>
                <th scope="row">{bar.label}</th>
                <td>{formatValue(totals[index] ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}

interface LegendEntry {
  readonly key: string;
  readonly label: string;
  readonly color: string;
}

/** One entry per band key, in the order the bands were first drawn. */
function legendOf(bars: readonly StackedBar[]): LegendEntry[] {
  const entries = new Map<string, LegendEntry>();
  for (const bar of bars) {
    for (const segment of bar.segments) {
      if (!entries.has(segment.key)) {
        entries.set(segment.key, { key: segment.key, label: segment.label, color: segment.color });
      }
    }
  }
  return [...entries.values()];
}
